import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Frame } from '@shopify/polaris';
import {
  HomeIcon,
  LayoutSidebarLeftIcon,
  MenuIcon,
  ExitIcon,
} from '@shopify/polaris-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthComponent';
import { groupedTools } from './tools';
import { AlertsProvider, useAlerts, navBadges } from './alerts';
import { Ic } from './ui';

const RAIL_KEY = 'uh-sidebar-rail';
const MOBILE_MAX = 767;

const isMobile = () => window.innerWidth <= MOBILE_MAX;

// Release stamp under the brand (vYYYYMMDD.N), and a check for newer ones.
// A deploy now reloads the backend gracefully, so an open tab keeps working
// through it — but the JS in memory is the old build. We poll /api/version and,
// when it moves past whatever we booted with, offer a reload. Never automatic:
// a surprise reload would throw away a half-filled form.
const VERSION_POLL_MS = 60000;

function useAppVersion() {
  const [version, setVersion] = useState(null);     // what this tab is running
  const [latest, setLatest] = useState(null);       // what the server has now
  const boot = useRef(null);

  useEffect(() => {
    let live = true;
    const read = async () => {
      try {
        const r = await fetch('/api/version', { cache: 'no-store' });
        if (!r.ok || !live) return;
        const d = await r.json();
        if (!live || !d || !d.version) return;
        if (boot.current === null) { boot.current = d.version; setVersion(d.version); }
        setLatest(d.version);
      } catch {
        /* a missing version must never break the shell */
      }
    };
    read();
    const id = setInterval(read, VERSION_POLL_MS);
    return () => { live = false; clearInterval(id); };
  }, []);

  return { version, latest, stale: !!(boot.current && latest && latest !== boot.current) };
}

function readRail() {
  try { return localStorage.getItem(RAIL_KEY) === '1'; } catch { return false; }
}

// Label tooltip for the collapsed rail — the only place labels are hidden.
function useRailTip(enabled) {
  const [tip, setTip] = useState(null);
  const show = (e, text) => {
    if (!enabled()) return;
    const r = e.currentTarget.getBoundingClientRect();
    setTip({ text, left: r.right + 8, top: r.top + r.height / 2 });
  };
  const hide = () => setTip(null);
  const node = tip && (
    <div className="uh-tip" style={{ left: tip.left, top: tip.top, transform: 'translateY(-50%)' }}>{tip.text}</div>
  );
  return { show, hide, node };
}

function NavItem({ path, icon, label, tipLabel, active, badge, stub, onNavigate, tip }) {
  return (
    <a
      href={path}
      className="uh-ni"
      aria-current={active ? 'page' : undefined}
      onClick={onNavigate(path)}
      onMouseEnter={(e) => tip.show(e, tipLabel || label)}
      onFocus={(e) => tip.show(e, tipLabel || label)}
      onMouseLeave={tip.hide}
      onBlur={tip.hide}
    >
      <Ic source={icon} />
      <span className="uh-lbl">{label}</span>
      {stub && <span className="uh-nsoon" title="Not built yet">Soon</span>}
      {badge && <span className={`uh-ncount${badge.tone === 'warn' ? ' warn' : ''}`} title={badge.label}>{badge.count}</span>}
      {badge && <span className={`uh-ndot${badge.tone === 'warn' ? ' warn' : ''}`} />}
    </a>
  );
}

function Shell({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, permissions, user, fullName, email } = useAuth();
  const { alerts } = useAlerts();
  const [rail, setRail] = useState(readRail);
  const [drawer, setDrawer] = useState(false);
  const { version, latest, stale } = useAppVersion();
  const [dismissed, setDismissed] = useState(null);
  const tip = useRailTip(useCallback(() => rail && !isMobile(), [rail]));

  const groups = groupedTools(permissions);
  const badges = navBadges(alerts);

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const currentTitle = (() => {
    if (location.pathname === '/') return 'Dashboard';
    if (location.pathname.startsWith('/profile')) return 'My Profile';
    for (const g of groups) for (const t of g.tools) if (isActive(t.path)) return t.title;
    return 'Utility Hub';
  })();

  const toggleRail = () => {
    setRail((v) => {
      try { localStorage.setItem(RAIL_KEY, v ? '0' : '1'); } catch { /* per-browser convenience only */ }
      return !v;
    });
    tip.hide();
  };

  const onNavigate = (path) => (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return; // let the browser open a new tab
    e.preventDefault();
    tip.hide();
    navigate(path);
    setDrawer(false);
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setDrawer(false); };
    const onResize = () => { if (!isMobile()) setDrawer(false); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('resize', onResize); };
  }, []);

  const displayName = fullName || user || 'Account';
  const initials = (fullName || user || '?').split(/[\s._-]+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  return (
    <div className={`uh uh-shell${rail ? ' collapsed' : ''}${drawer ? ' drawer' : ''}`}>
      <div className="uh-scrim" onClick={() => setDrawer(false)} />
      <aside className="uh-side" aria-label="Main navigation">
        <div className="uh-side-h">
          <img className="uh-mark" src="/images/soccer-post-mark.webp" alt="Soccer Post" width="32" height="32" />
          <div className="uh-brand">
            <div className="uh-brand-n">Utility Hub</div>
            <div className="uh-brand-s">{version ? `v${version}` : ''}</div>
          </div>
          <button
            type="button"
            className="uh-ibtn uh-collapse-btn"
            onClick={toggleRail}
            aria-label={rail ? 'Expand menu' : 'Collapse menu'}
            onMouseEnter={(e) => tip.show(e, 'Expand menu')}
            onMouseLeave={tip.hide}
          >
            <Ic source={LayoutSidebarLeftIcon} />
          </button>
        </div>

        <nav className="uh-nav">
          <NavItem path="/" icon={HomeIcon} label="Dashboard" active={isActive('/')} onNavigate={onNavigate} tip={tip} />
          {groups.map((g) => (
            <React.Fragment key={g.id}>
              <div className="uh-nav-g">{g.title}</div>
              <div className="uh-nav-sep" />
              {g.tools.map((t) => (
                <NavItem
                  key={t.id}
                  path={t.path}
                  icon={t.icon}
                  label={t.navTitle}
                  tipLabel={badges[t.id] ? `${t.title} · ${badges[t.id].label}` : t.title}
                  active={isActive(t.path)}
                  badge={badges[t.id]}
                  stub={t.stub}
                  onNavigate={onNavigate}
                  tip={tip}
                />
              ))}
            </React.Fragment>
          ))}
        </nav>

        <div className="uh-side-f">
          <div className="uh-user">
            <a
              href="/profile"
              className="uh-user-link"
              aria-current={isActive('/profile') ? 'page' : undefined}
              aria-label={`${displayName}, my profile`}
              onClick={onNavigate('/profile')}
              onMouseEnter={(e) => tip.show(e, displayName)}
              onFocus={(e) => tip.show(e, displayName)}
              onMouseLeave={tip.hide}
              onBlur={tip.hide}
            >
              <span className="uh-avatar" aria-hidden="true">{initials}</span>
              <span className="uh-user-t">
                <b>{displayName}</b>
                <span>{email || user}</span>
              </span>
            </a>
            <button
              type="button"
              className="uh-ibtn"
              aria-label="Sign out"
              onClick={() => { logout(); navigate('/login'); }}
              onMouseEnter={(e) => tip.show(e, 'Sign out')}
              onFocus={(e) => tip.show(e, 'Sign out')}
              onMouseLeave={tip.hide}
              onBlur={tip.hide}
            >
              <Ic source={ExitIcon} />
            </button>
          </div>
        </div>
      </aside>

      <div className="uh-main">
        <div className="uh-mtop">
          <button type="button" className="uh-ibtn" onClick={() => setDrawer(true)} aria-label="Open menu">
            <Ic source={MenuIcon} />
          </button>
          <span className="uh-t">{currentTitle}</span>
        </div>
        <Frame>{children}</Frame>
      </div>
      {stale && dismissed !== latest && (
        <div className="uh-update" role="status">
          <span>A new version is available.</span>
          <button type="button" className="uh-btn primary slim" onClick={() => window.location.reload()}>Reload</button>
          <button type="button" className="uh-btn slim" onClick={() => setDismissed(latest)}>Later</button>
        </div>
      )}
      {tip.node}
    </div>
  );
}

function AppShell({ children }) {
  const { permissions } = useAuth();
  return (
    <AlertsProvider permissions={permissions}>
      <Shell>{children}</Shell>
    </AlertsProvider>
  );
}

export default AppShell;
